/**
 * Workflow utility functions for offboarding processes
 * Provides helpers for step initialization, validation, and navigation
 */

/**
 * Get route for a specific workflow step
 * @param {string} workflowId - The workflow ID
 * @param {number} stepNumber - The step number (1-7)
 * @returns {string} Route path
 */
export const getStepRoute = (workflowId, stepNumber) => {
  return `/admin/employee-lifecycle/offboarding/${workflowId}/step-${stepNumber}`;
};

/**
 * Validate if navigation to target step is allowed
 * Users can navigate to any completed step or the current step
 * @param {number} currentStep - Current workflow step
 * @param {number} targetStep - Target step to navigate to
 * @param {Object} workflow - Workflow object
 * @returns {boolean} Whether navigation is allowed
 */
export const validateStepNavigation = (currentStep, targetStep, workflow) => {
  if (!workflow) return false;
  
  // Always allow navigation to current step
  if (targetStep === currentStep) return true;
  
  // Allow navigation to any previously completed step
  const targetStepCompleted = workflow[`step_${targetStep}_completed`];
  if (targetStepCompleted) return true;
  
  // Allow navigation to the next step if current step is completed
  if (targetStep === currentStep + 1 && workflow[`step_${currentStep}_completed`]) {
    return true;
  }
  
  return false;
};

/**
 * Initialize step data from workflow data
 * Provides default values for missing fields
 * @param {Object} workflowData - The workflow data
 * @param {number} stepNumber - The step number
 * @returns {Object} Initialized step data
 */
export const initializeStepData = (workflowData, stepNumber) => {
  if (!workflowData) return {};

  switch (stepNumber) {
    case 1:
      return {
        resignation_date: workflowData.resignation_date || '',
        resignation_reason: workflowData.resignation_reason || '',
        resignation_notes: workflowData.resignation_notes || '',
        resignation_letter_filename: workflowData.resignation_letter_filename || '',
        resignation_letter_uploaded: workflowData.resignation_letter_uploaded || false
      };

    case 2:
      return {
        final_working_day: workflowData.final_working_day || workflowData.employment_end_date || '',
        final_salary_payment_date: workflowData.final_salary_payment_date || '',
        handover_period_days: workflowData.handover_period_days || '',
        exit_interview_scheduled: workflowData.exit_interview_scheduled || '',
        hod_discussion_date: workflowData.hod_discussion_date || '',
        hod_staff_id: workflowData.hod_staff_id || '',
        primary_reason: workflowData.primary_reason || '',
        departure_discussion_summary: workflowData.departure_discussion_summary || '',
        emotional_state: workflowData.emotional_state || '',
        emotional_support_provided: workflowData.emotional_support_provided || false,
        counselling_recommended: workflowData.counselling_recommended || false,
        annual_leave_balance_days: workflowData.annual_leave_balance_days || '',
        leave_settlement_preference: workflowData.leave_settlement_preference || '',
        leave_timing_constraints: workflowData.leave_timing_constraints || '',
        urgent_handover_needed: workflowData.urgent_handover_needed || false,
        successor_identified: workflowData.successor_identified || false,
        handover_details: workflowData.handover_details || '',
        hod_recommendations: workflowData.hod_recommendations || ''
      };

    case 3:
      return {
        hod_discussion_date: workflowData.hod_discussion_date || '',
        hod_staff_id: workflowData.hod_staff_id || '',
        leave_encashment_approved: workflowData.leave_encashment_approved || false,
        leave_offset_approved: workflowData.leave_offset_approved || false,
        employment_end_date: workflowData.final_working_day || workflowData.employment_end_date || '',
        final_salary_payment_date: workflowData.final_salary_payment_date || '',

        // Exit Interview Preparation
        exit_interview_scheduled: workflowData.exit_interview_scheduled || null,
        interview_format: workflowData.interview_format || 'in_person',
        questionnaire_sent: workflowData.questionnaire_sent || '',
        questionnaire_sent_date: workflowData.questionnaire_sent_date || '',

        // During Interview
        interview_welcome_completed: workflowData.interview_welcome_completed || false,
        interview_welcome_notes: workflowData.interview_welcome_notes || '',
        interview_confidentiality_explained: workflowData.interview_confidentiality_explained || false,
        interview_confidentiality_notes: workflowData.interview_confidentiality_notes || '',
        interview_purpose_explained: workflowData.interview_purpose_explained || false,
        interview_purpose_notes: workflowData.interview_purpose_notes || '',
        feedback_career_guidance: workflowData.feedback_career_guidance || false,
        feedback_career_guidance_notes: workflowData.feedback_career_guidance_notes || '',
        feedback_career_pathways: workflowData.feedback_career_pathways || false,
        feedback_career_pathways_notes: workflowData.feedback_career_pathways_notes || '',
        feedback_workplace_flexibility: workflowData.feedback_workplace_flexibility || false,
        feedback_workplace_flexibility_notes: workflowData.feedback_workplace_flexibility_notes || '',
        feedback_workplace_culture: workflowData.feedback_workplace_culture || false,
        feedback_workplace_culture_notes: workflowData.feedback_workplace_culture_notes || '',
        feedback_recognition: workflowData.feedback_recognition || false,
        feedback_recognition_notes: workflowData.feedback_recognition_notes || '',
        feedback_image_reputation: workflowData.feedback_image_reputation || false,
        feedback_image_reputation_notes: workflowData.feedback_image_reputation_notes || '',
        feedback_others: workflowData.feedback_others || false,
        feedback_others_notes: workflowData.feedback_others_notes || '',
        interview_notes: workflowData.interview_notes || '',

        // Employee Support
        reference_letter_assistance: workflowData.reference_letter_assistance || false
      };

    case 4:
      return {
        active_bonds_detected: workflowData.active_bonds_detected || false,
        bond_breach_amount: workflowData.bond_breach_amount || '',
        bond_settlement_method: workflowData.bond_settlement_method || '',
        bond_calculation_details: workflowData.bond_calculation_details || ''
      };

    case 5:
      return {
        final_salary_amount: workflowData.final_salary_amount || '',
        total_leave_encashment: workflowData.total_leave_encashment || '',
        total_bond_deduction: workflowData.total_bond_deduction || '',
        net_final_payment: workflowData.net_final_payment || '',
        settlement_calculation_details: workflowData.settlement_calculation_details || '',
        settlement_approved: workflowData.settlement_approved || false,
        settlement_approved_by: workflowData.settlement_approved_by || '',
        settlement_approved_date: workflowData.settlement_approved_date || null
      };

    case 6:
      return {
        requires_ir21: workflowData.requires_ir21 || false,
        ir21_form_generated: workflowData.ir21_form_generated || false,
        ir21_submitted_date: workflowData.ir21_submitted_date || '',
        work_permit_cancelled: workflowData.work_permit_cancelled || false,
        exit_permit_arranged: workflowData.exit_permit_arranged || false,
        ir21_reference_number: workflowData.ir21_reference_number || '',
        immigration_notes: workflowData.immigration_notes || '',
        compliance_checklist: workflowData.compliance_checklist || {
          work_permit_verified: false,
          exit_permit_applied: false,
          embassy_notified: false,
          documents_collected: false,
          final_clearance_obtained: false
        }
      };

    case 7:
      return {
        exit_interview_completed: workflowData.exit_interview_completed || false,
        exit_interview_date: workflowData.exit_interview_date || '',
        company_assets_returned: workflowData.company_assets_returned || false,
        system_access_revoked: workflowData.system_access_revoked || false,
        final_payment_processed: workflowData.final_payment_processed || false,
        employment_terminated: workflowData.employment_terminated || false,
        final_exit_notes: workflowData.final_exit_notes || '',
        assets_checklist: workflowData.assets_checklist || {
          laptop_returned: false,
          mobile_phone_returned: false,
          access_card_returned: false,
          keys_returned: false,
          documents_returned: false,
          other_assets_returned: false
        },
        access_checklist: workflowData.access_checklist || {
          email_access_revoked: false,
          system_access_revoked: false,
          building_access_revoked: false,
          software_licenses_revoked: false,
          vendor_access_revoked: false
        }
      };

    default:
      return {};
  }
};

/**
 * Check if a workflow step has required fields filled
 * @param {Object} stepData - Step data object
 * @param {number} stepNumber - The step number
 * @returns {boolean} Whether required fields are filled
 */
export const validateStepData = (stepData, stepNumber) => {
  switch (stepNumber) {
    case 1:
      return !!(
        stepData.resignation_date &&
        stepData.resignation_reason &&
        (stepData.resignation_letter_uploaded || stepData.resignation_letter_file)
      );

    case 2:
      return !!(
        stepData.hod_discussion_date &&
        stepData.hod_staff_id
      );

    case 3:
      return !!(
        stepData.employment_end_date &&
        stepData.final_salary_payment_date
      );

    case 4:
      // Bond check is automatic, always valid
      return true;

    case 5:
      return !!(
        stepData.final_salary_amount &&
        stepData.net_final_payment
      );

    case 6:
      // IR21 validation depends on whether it's required
      if (!stepData.requires_ir21) return true;
      return !!(
        stepData.ir21_form_generated &&
        stepData.work_permit_cancelled
      );

    case 7:
      return !!(
        stepData.exit_interview_completed &&
        stepData.company_assets_returned &&
        stepData.system_access_revoked &&
        stepData.final_payment_processed
      );

    default:
      return false;
  }
};

